export default {
    chapters: {
        problem: "Problem",
        solution: "Solution",
        features: "Features",
        world: "AI Route",
        pricing: "Pricing",
        cta: "Activate",
    },
    hero: {
        badge: "AI Workforce for logistics",
        title: "Stop chasing emails. Start steering shipments.",
        description: "EveMind unifies emails, documents, tracking, and operations into one AI-powered execution flow.",
        titleLine1: "Stop chasing emails.",
        titleLine2: "Start steering shipments.",
        typewriterPrefix: "Start",
        typewriterSuffixes: [
            "steering shipments.",
            "reducing delays.",
            "protecting margins.",
            "aligning every handoff.",
            "acting before risk hits.",
        ],
        subtitle: "EveMind unifies emails, documents, tracking, and operations into one AI-powered execution flow.",
        imageAlt: "Container ship at sea — maritime freight and global logistics",
        ctaPrimary: "Activate account",
        ctaExplore: "Explore features",
        ctaLogin: "Log in",
        ctaGoToApp: "Go to app",
        ctaContact: "Contact us",
    },
    problem: {
        label: "The Problem",
        title: "Fragmented operations, slow decisions, shrinking margins.",
        description: "With endless threads, scattered attachments, and manual updates, logistics teams react too late instead of preventing risk.",
        cards: {
            fragmented: {
                title: "Data spread across disconnected channels",
                description: "Email, Excel, PDF, and chat never share a single operational context.",
            },
            late: {
                title: "Alerts arrive after impact",
                description: "Critical issues surface when costs and delays are already in motion.",
            },
            manual: {
                title: "Too much repetitive manual work",
                description: "Teams spend time updating status instead of driving outcomes.",
            },
        },
    },
    solution: {
        label: "EveMind Solution",
        title: "An AI console that sees earlier, decides faster, and executes better.",
        description: "EveMind creates a single operational view per shipment and activates the best next action automatically.",
        bullets: {
            ingest: {
                title: "Automated multi-source ingestion",
                description: "Connect emails, documents, and tracking events in real-time.",
            },
            decide: {
                title: "Context-aware decision intelligence",
                description: "Prioritize risk and surface high-impact actions for each team.",
            },
            act: {
                title: "AI-assisted execution",
                description: "Respond, update, validate, and coordinate without losing context.",
            },
        },
        timelineLabel: "From signal to action",
        timeline: {
            capture: { time: "T+0 min", title: "New email and document captured" },
            validate: { time: "T+2 min", title: "Automatic cross-check and anomaly detection" },
            predict: { time: "T+5 min", title: "Risk score refreshed with explanation" },
            notify: { time: "T+6 min", title: "Alert and next action sent to the right team" },
        },
    },
    agents: {
        care: {
            title: "Eve Care",
            subtitle: "AI Customer Care Agent",
            description: "Handles customer requests, aligns teams, and keeps conversation history linked to live shipment context.",
            features: [
                "Responds to customer requests in real-time",
                "Automatically updates customers on shipment status",
                "Records all interactions automatically",
            ],
        },
        track: {
            title: "Eve Track",
            subtitle: "Real-Time Tracking Agent",
            description: "Unifies multi-carrier tracking, updates ETA predictions, and flags route anomalies before they become incidents.",
            features: [
                "Connects satellite data and GPS in real-time",
                "Automatically responds to customer update requests",
                "Generates notifications for delays and anomalies",
            ],
        },
        quote: {
            title: "Eve Quote",
            subtitle: "Quotation Automation Agent",
            description: "Reads inbound quote requests, computes pricing with your logic, and sends ready-to-approve offers fast.",
            features: [
                "Calculates rates in seconds using your pricing rules",
                "Generates professional offers ready to send",
                "Handles modifications and updates automatically",
            ],
        },
        read: {
            title: "Eve Read",
            subtitle: "Document Reading Agent",
            description: "Extracts critical fields from trade documents, catches mismatches, and feeds systems without manual re-entry.",
            features: ["Reads documents in any format automatically", "Extracts and structures key data instantly", "Sends data directly to your TMS"],
        },
        plan: {
            title: "Eve Plan",
            subtitle: "Route Planning Agent",
            description: "Builds optimal daily plans, reprioritizes instantly on changes, and keeps execution aligned with capacity.",
            features: [
                "Suggests the most efficient travel plans daily",
                "Recalculates priorities in real-time when conditions change",
                "Considers vehicle availability and operational restrictions",
            ],
        },
    },
    features: {
        label: "How we solve it",
        title: "One AI agents fleet, one operational control layer",
        description: "Each agent owns a mission-critical stage; together they reduce errors, delays, and coordination overhead.",
        showcase: {
            learnMore: "Ready to activate EveMind?",
            care: {
                mockLabel: "Live inbox",
                messages: [
                    "Customer: What’s the latest ETA for MSCU7654321?",
                    "Eve Care: Vessel sequence updated — new ETA 14 Apr, 09:40 local.",
                    "Customer: Please forward the revised B/L draft when ready.",
                ],
            },
            track: {
                mockLabel: "Tracking stream",
                lines: ["Gate out Mombasa · status OK", "Transshipment Colombo · deviation −6h flagged", "ATD refreshed · liner feed v2 applied"],
            },
            quote: {
                mockLabel: "Quote API",
                responses: [
                    'HTTP 200 { "quoteId": "q_9f2c4a1b-c3d5-…", "status": "draft_ready" }',
                    'HTTP 200 { "quoteId": "q_9f2c4a1b-c3d5-…", "status": "sent", "margin": 0.124 }',
                ],
            },
            read: {
                mockLabel: "Extraction pipeline",
                chips: ["B/L", "Invoice", "Packing list"],
                rows: [
                    { tone: "ok", title: "Invoice ↔ Packing list", meta: "Weights aligned" },
                    { tone: "warn", title: "B/L shipper spelling", meta: "Human review queued" },
                    { tone: "ok", title: "Phytosanitary", meta: "Logged + filed" },
                ],
            },
            plan: {
                mockLabel: "Priority board",
                rows: [
                    { rank: "P1", text: "Reefer pickup — Genoa CY, 07:30 window" },
                    { rank: "P2", text: "Booking amendment — MSC #448821 follow-up" },
                    { rank: "P3", text: "Docs bundle — customer ref ASH-2026-044" },
                ],
            },
        },
    },
    world: {
        label: "3D Storytelling",
        title: "One intelligent route that evolves as you scroll.",
        description: "Scroll the route from Mombasa to San Francisco: each stop matches a stage EveMind covers on a real transoceanic shipping lane.",
        stopPrefix: "Stop {{index}}",
        stops: {
            care: {
                title: "Customer communication always aligned",
                description: "Customers receive clear, contextual updates without manual follow-ups.",
                place: "Mombasa, Kenya — port of loading",
            },
            track: {
                title: "End-to-end route visibility",
                description: "Multi-source tracking plus proactive delay and deviation alerts.",
                place: "Colombo, Sri Lanka — Indian Ocean transit",
            },
            quote: {
                title: "Fast and consistent quoting",
                description: "From request to quote in minutes, with margin logic enforced.",
                place: "Singapore — Malacca Strait",
            },
            read: {
                title: "Automated document verification",
                description: "Cross-check and anomaly detection before issues become losses.",
                place: "Shanghai, China — Pacific gateway",
            },
            plan: {
                title: "Dynamic operational planning",
                description: "Priorities and resources adapt in real-time to changing conditions.",
                place: "San Francisco, USA — arrival and discharge",
            },
        },
    },
    pricing: {
        label: "Pricing",
        title: "A commercial model designed around your operation.",
        description: "No one-size-fits-all plans. We align scope, integrations, and automation depth to your logistics workflow.",
        cards: {
            volume: {
                title: "Volume-based",
                description: "Scales with shipment count, document flow, and interactions.",
            },
            operations: {
                title: "Complexity-based",
                description: "Aligned with teams, SOP maturity, and SLA requirements.",
            },
            integration: {
                title: "Integration-based",
                description: "TMS/ERP connections and onboarding depth tuned to your stack.",
            },
        },
        ctaPrimary: "Talk to sales",
        ctaSecondary: "Activate account",
    },
    finalCta: {
        title: "Ready to turn logistics into a strategic advantage?",
        description: "Activate EveMind and run risk, docs, and tracking through one AI-powered operations layer.",
        ctaPrimary: "Activate account",
        ctaSecondary: "Book a demo",
    },
    footer: {
        legal: "Legal",
        contactTitle: "Ready to transform your logistics?",
        contactDescription: "Get in touch with our team to learn more about EveMind.",
        contactButton: "Contact us",
        loginLink: "Already have an account?",
        loginButton: "Log in",
    },
};
